import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useIonRouter } from "@/app/IonicContextProvider";
import { authApi } from "./api";
import type { AuthInitializeRequest, AuthLoginRequest, AuthUser } from "./types";
import { authService } from "@/lib/auth";
import { queryClient } from "@/shared/queryClient";
import { useAuthContext } from "@/lib/authContext";
import { storageService } from "@/lib/storage";
import { notificationService } from "@/lib/notifications";
import { useToast } from "@/hooks/use-toast";
import { isTransportError } from "@/shared/utils/networkErrors";
import { extractErrorMessage } from "@/shared/utils/errors";
import { saveOfflineSessionVault, tryUnlockOfflineSession } from "@/lib/offlineSessionVault";
import { logger } from "@/shared/logger";

// Token management
export function useAuthToken() {
  const getToken = async () => {
    const token = await authService.getStoredToken();
    return token;
  };
  
  const setToken = async (token: string) => {
    // Token will be saved when saving the session
    return token;
  };
  
  const removeToken = async () => {
    await authService.clearSession();
  };
  
  return { getToken, setToken, removeToken };
}

// Current user hook
export function useCurrentUser() {
  const { getToken } = useAuthToken();
  
  return useQuery({
    queryKey: ["auth", "user"],
    queryFn: async (): Promise<AuthUser | null> => {
      const token = await getToken();
      if (!token) {
        return null;
      }
      
      // First try to get user from stored data
      const storedUser = await authService.getStoredUser();
      if (storedUser) {
        return storedUser;
      }
      
      // Fallback to decoding token
      try {
        const payload = JSON.parse(atob(token.split(".")[1]));
        const user = payload.user || payload.employee || null;
        return user;
      } catch (error) {
        console.error("Error decoding token:", error);
        return null;
      }
    },
    staleTime: Infinity,
    gcTime: Infinity, // Add this so it never disappears
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false, // Add this too
    initialData: () => {
      const cachedUser = queryClient.getQueryData(["auth", "user"]);
      return cachedUser as AuthUser | null;
    },
  });
}

// Authentication status
export function useAuth() {
  const { data: user, isLoading } = useCurrentUser();
  const { logout: contextLogout } = useAuthContext();
  const router = useIonRouter();
  
  
  const logout = async () => {
    await contextLogout();
    router.push("/login", "root", "replace");
  };
  
  return {
    user,
    isAuthenticated: !!user,
    isLoading,
    logout,
  };
}

// Initialize mutation
export function useInitialize() {
  const router = useIonRouter();
  const { setToken } = useAuthToken();
  const { setAuthenticated } = useAuthContext();
  
  return useMutation({
    mutationFn: authApi.initialize,
    onSuccess: async (response, variables) => {
      logger.info("auth.initialize.success", {
        employeeId: response.employee?.id,
        hasAccessToken: !!response.access_token,
      });
      await setToken(response.access_token);
      
      // Store user in query cache and persistent storage (display name from API, phone as fallback)
      const userData = {
        ...response.employee,
        name:
          response.employee.name?.trim() || response.employee.whatsapp_number,
      };
      await authService.saveSession(response.access_token, userData);
      await saveOfflineSessionVault(variables.whatsapp_number, variables.pin_code, {
        access_token: response.access_token,
        employee: response.employee,
      });
      logger.info("auth.initialize.sessionPersisted", {
        employeeId: userData.id,
        phone: userData.whatsapp_number,
        displayName: userData.name,
      });
      
      // Set authenticated state (no need to reset first)
      setAuthenticated(userData);
      
      // Backfill device registration if not already registered for this employee (fire-and-forget)
      const employeeId = response.employee?.id;
      if (typeof employeeId === 'number') {
        setTimeout(() => {
          notificationService.ensureDeviceRegisteredIfNeeded(employeeId).catch(() => {});
        }, 1000);
      }
      
      // Navigate with Ionic router — replace the Login page
      router.push("/shifts", "root", "replace");
    },
    onError: (error) => {
      logger.error("auth.initialize.error", { error });
    },
  });
}

// Login mutation
export function useLogin() {
  const router = useIonRouter();
  const { setToken } = useAuthToken();
  const { setAuthenticated } = useAuthContext();
  const { toast } = useToast();
  
  return useMutation({
    mutationFn: async (variables: AuthLoginRequest) => {
      logger.info("auth.login.start", {
        phone: variables.whatsapp_number,
        hasDeviceId: !!variables.device_id,
        platform: variables.platform,
      });
      try {
        const response = await authApi.login(variables);
        logger.info("auth.login.apiSuccess", {
          employeeId: response.employee?.id,
          deviceTrusted: response.device_trusted,
        });
        return response;
      } catch (e) {
        logger.warn("auth.login.apiError", { error: e });
        if (isTransportError(e)) {
          logger.warn("auth.login.transportDetected", {
            phone: variables.whatsapp_number,
          });
          const offline = await tryUnlockOfflineSession(
            variables.whatsapp_number,
            variables.pin_code
          );
          if (offline) {
            logger.info("auth.login.offlineVaultUnlocked", {
              employeeId: offline.employee?.id,
            });
            return offline;
          }
          logger.warn("auth.login.offlineVaultUnavailable", {
            phone: variables.whatsapp_number,
          });
          const base = extractErrorMessage(e);
          throw new Error(
            `${base} If you signed in on this device before, check your PIN. If your session has expired, connect to the internet to sign in again.`
          );
        }
        throw e;
      }
    },
    onSuccess: async (response, variables) => {
      logger.info("auth.login.success", {
        employeeId: response.employee?.id,
        hasAccessToken: !!response.access_token,
        deviceTrusted: response.device_trusted,
      });
      await setToken(response.access_token);
      
      // Store user in query cache and persistent storage (display name from API, phone as fallback)
      const userData = {
        ...response.employee,
        name:
          response.employee.name?.trim() || response.employee.whatsapp_number,
      };
      await authService.saveSession(response.access_token, userData);
      await saveOfflineSessionVault(variables.whatsapp_number, variables.pin_code, {
        access_token: response.access_token,
        employee: response.employee,
      });
      logger.info("auth.login.sessionPersisted", {
        employeeId: userData.id,
        phone: userData.whatsapp_number,
        displayName: userData.name,
      });
      
      // Save phone number for future logins
      await storageService.setPhoneNumber(variables.whatsapp_number);
      
      // Handle device trust status
      if (!response.device_trusted && response.message) {
        // Show notification about pending device approval
        toast({
          title: "Device Approval Required",
          description: response.message,
          variant: "default",
        });
      }
      
      // Set authenticated state (no need to reset first)
      setAuthenticated(userData);
      
      // Backfill device registration if not already registered for this employee (fire-and-forget)
      const employeeId = response.employee?.id;
      if (typeof employeeId === 'number') {
        setTimeout(() => {
          notificationService.ensureDeviceRegisteredIfNeeded(employeeId).catch(() => {});
        }, 1000);
      }
      
      // Replace the login route in history
      router.push("/shifts", "root", "replace");
    },
    onError: (error, vars) => {
      logger.error("auth.login.finalError", {
        phone: vars?.whatsapp_number,
        error,
      });
    },
  });
}

// FCM token hook (stub for Ionic migration)
export function useFcmToken(): string {
  // Return empty string for now, will be implemented with Capacitor later
  return "";
}
